/**
 * TabStrip — the horizontal navigation strip at the top of the Shipments page.
 *
 * Renders:
 *   1. Default tabs ("All shipments", "Active") — system-managed, no actions.
 *   2. Custom tabs (user's pinned saved filter sets) — with hover actions.
 *   3. PlusMenu button — pin existing filter set or create a new one.
 *
 * Connects to Redux `shipmentTab` slice.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import styles from './TabStrip.module.scss';
import { message } from 'antd';
import DefaultTabItem from './DefaultTabItem';
import CustomTabItem from './CustomTabItem';
import PlusMenu from './PlusMenu';
import {
  addTab,
  removeTab,
  setActiveTabId,
  setCustomTabs,
  setDefaultTabs,
  setTabsLoading,
} from '@/redux-toolkit/slice/shipmentTabSlice';
import { RootState } from '@/redux-toolkit/store';
import {
  duplicateSavedFilterSet,
  getAvailableFilterSets,
  getShipmentTabs,
  hideShipmentTab,
  pinFilterSetAsTab,
} from '@/apis/shipmentTabs';
import type {
  AppliedFilter,
  AvailableFilterSetsResponse,
  DefaultTab,
  FilterArrayItem,
  SavedFilterSet,
  ShipmentTab,
} from '@/pages/Shipments/ControlTower/types';
import { reorderTabs } from '@/apis/shipment';

const DEFAULT_TABS: DefaultTab[] = [
  { id: 'all_shipments', label: 'All shipments', isDefault: true },
  { id: 'active', label: 'Active', isDefault: true },
];

interface Props {
  tableView?: boolean;
  permissions: unknown;
  token: string;
  /** Count to show on the active tab chip */
  activeTabCount?: number;
  /** Called when the user switches tabs */
  onTabChange: (tabId: string, tabOverride?: ShipmentTab) => void;
  /** Current applied filters — retained for parent compatibility */
  currentAppliedFilters?: AppliedFilter[];
  currentFilterArray?: FilterArrayItem[];
  /** Parent-owned modal triggers */
  onOpenCreateModal: () => void;
  onOpenEditModal: (tab: ShipmentTab | SavedFilterSet) => void;
  onOpenViewFiltersModal: (tab: ShipmentTab) => void;
  /** Function to expose refetch capability to parent */
  onRefetchReady?: (refetch: () => Promise<void>) => void;
}

const TabStrip: React.FC<Props> = ({
  tableView = false,
  token,
  activeTabCount,
  onTabChange,
  onOpenCreateModal,
  onOpenEditModal,
  onOpenViewFiltersModal,
  onRefetchReady,
}) => {
  const dispatch = useDispatch();
  const { customTabs, activeTabId } = useSelector(
    (state: RootState) => state.shipmentTab
  );
  const [tabsLoaded, setTabsLoaded] = useState(false);
  const didNormalizeInitialTabRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // ------------------------------------------------------------------
  // Load tabs on mount
  // ------------------------------------------------------------------
  const refetchTabs = useCallback(async () => {
    dispatch(setTabsLoading(true));
    try {
      const data = await getShipmentTabs(token);
      dispatch(setDefaultTabs(data.defaultTabs));
      dispatch(setCustomTabs(data.customTabs));
    } catch {
      // Silently fail — user just sees no custom tabs
    } finally {
      dispatch(setTabsLoading(false));
      setTabsLoaded(true);
    }
  }, [token, dispatch]);

  useEffect(() => {
    refetchTabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose refetch function to parent
  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(refetchTabs);
    }
  }, [refetchTabs, onRefetchReady]);

  useEffect(() => {
    if (!tabsLoaded || didNormalizeInitialTabRef.current) return;
    didNormalizeInitialTabRef.current = true;

    const isMissingCustomTab =
      activeTabId !== 'all_shipments' &&
      activeTabId !== 'active' &&
      !customTabs.some((tab) => tab.id === activeTabId);

    if (!isMissingCustomTab) return;

    dispatch(setActiveTabId('all_shipments'));
    onTabChange('all_shipments');
  }, [activeTabId, customTabs, dispatch, onTabChange, tabsLoaded]);

  // ------------------------------------------------------------------
  // Tab click
  // ------------------------------------------------------------------
  const handleTabClick = (tabId: string) => {
    dispatch(setActiveTabId(tabId));
    onTabChange(tabId);
  };
  // ------------------------------------------------------------------
  // Plus menu handlers
  // ------------------------------------------------------------------
  const fetchAvailable = useCallback(
    (): Promise<AvailableFilterSetsResponse> => getAvailableFilterSets(token),
    [token]
  );

  const handleSelectFilterSet = async (filterSetId: string) => {
    try {
      const tab = await pinFilterSetAsTab(token, filterSetId);
      dispatch(addTab(tab));
      dispatch(setActiveTabId(tab.id));
      onTabChange(tab.id, tab);
    } catch {
      message.error('Failed to add tab. Please try again.');
    }
  };
  // ------------------------------------------------------------------
  // Tab action handlers
  // ------------------------------------------------------------------
  const handleHide = async (tab: ShipmentTab) => {
    try {
      await hideShipmentTab(token, tab.id);
      dispatch(removeTab(tab.id));
    } catch {
      message.error('Failed to hide tab. Please try again.');
    }
  };

  const handleDuplicate = async (tab: ShipmentTab) => {
    try {
      const { tab: newTab } = await duplicateSavedFilterSet(
        token,
        tab.savedFilterSet.id
      );
      dispatch(addTab(newTab));
      dispatch(setActiveTabId(newTab.id));
      onTabChange(newTab.id);
    } catch {
      message.error('Failed to duplicate tab. Please try again.');
    }
  };

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = customTabs.findIndex((tab) => tab.id === active.id);
      const newIndex = customTabs.findIndex((tab) => tab.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const previousTabs = customTabs;
      const reorderedTabs = arrayMove(customTabs, oldIndex, newIndex);

      dispatch(setCustomTabs(reorderedTabs));

      try {
        await reorderTabs(token, {
          tab_order: reorderedTabs.map((tab) => tab.id),
        });
      } catch {
        dispatch(setCustomTabs(previousTabs));
        message.error('Failed to reorder tabs. Please try again.');
      }
    },
    [customTabs, dispatch, token]
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div
      data-testid='tab-strip'
      role='tablist'
      className={`${styles.tabStrip} ${tableView ? 'responsive-tab-padding' : ''}`}
    >
      {/* Default tabs */}
      {DEFAULT_TABS.map((tab) => (
        <DefaultTabItem
          key={tab.id}
          tab={tab}
          isActive={activeTabId === tab.id}
          shipmentCount={activeTabId === tab.id ? activeTabCount : undefined}
          onClick={() => handleTabClick(tab.id)}
        />
      ))}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={customTabs.map((tab) => tab.id)}
          strategy={horizontalListSortingStrategy}
        >
          {customTabs.map((tab) => (
            <CustomTabItem
              key={tab.id}
              tab={tab}
              isActive={activeTabId === tab.id}
              shipmentCount={
                activeTabId === tab.id ? activeTabCount : undefined
              }
              onClick={() => handleTabClick(tab.id)}
              onEdit={() => onOpenEditModal(tab)}
              onHide={() => handleHide(tab)}
              onDuplicate={() => handleDuplicate(tab)}
              onViewFilters={() => onOpenViewFiltersModal(tab)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Plus menu */}
      <PlusMenu
        tableView={tableView}
        onFetchAvailable={fetchAvailable}
        onSelectFilterSet={handleSelectFilterSet}
        onOpenCreateModal={onOpenCreateModal}
        onOpenEditModal={onOpenEditModal}
      />
    </div>
  );
};

export default TabStrip;
